import { memo, useCallback } from 'react'

import { useQuery } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Header } from '@shared/components/Header'
import { Tabs } from '@shared/components/MaterialUIEnhancements/Tabs'
import { Content } from '@shared/layouts'
import { NavigationTabLink } from 'components/LinkedMUIComponents/NavigationTabLink'
import { ActionsMenu } from 'domains/ContentStores/ExternalStores/ExternalStore'
import { useExternalStoreUpdateMutation } from 'hooks/mutations/content'
import {
  externalStoreConfigRoute,
  externalStoreRoute,
} from 'pages/NWPlatform/ContentStores/ExternalStores/ExternalStore'
import { externalStoreQueryKeys } from 'services/queryKeys'

const StructureRaw = () => {
  const { t } = useTranslation(['entity', 'pages'])
  const { enqueueSnackbar } = useSnackbar()

  const { externalStoreId } = externalStoreRoute.useParams()

  const { data, isLoading, isPending } = useQuery(externalStoreQueryKeys.detail({ storeId: externalStoreId }))

  const { mutate: updateExternalStore } = useExternalStoreUpdateMutation()

  const handleUpdateExternalStoreName = useCallback(
    (title: string, closeCallback: () => void) => {
      updateExternalStore(
        {
          storeId: externalStoreId,
          contentExternalStoreEdit: {
            ...data,
            name: title,
          },
        },
        {
          onError: () => enqueueSnackbar(t('pages:ContentStores.updateError')),
          onSettled: closeCallback,
        },
      )
    },
    [updateExternalStore, externalStoreId, data, enqueueSnackbar, t],
  )

  return (
    <Content
      HeaderSlot={
        <Header
          title={data?.name ?? ''}
          isLoading={isLoading}
          isSaving={isPending}
          onSave={handleUpdateExternalStoreName}
          TabNavigation={
            <Tabs>
              <NavigationTabLink from={externalStoreRoute.to} to={externalStoreConfigRoute.to}>
                {t('pages:ContentStores.config')}
              </NavigationTabLink>
            </Tabs>
          }
          Actions={<ActionsMenu />}
        />
      }
    />
  )
}

export const Structure = memo(StructureRaw)
